import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Box, 
  Typography, 
  Paper,
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Chip,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  Grid,
  Divider,
  Pagination,
  MenuItem,
  Menu,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Visibility as VisibilityIcon,
  Print as PrintIcon,
  Receipt as ReceiptIcon,
  Download as DownloadIcon,
  Add as AddIcon,
  AssignmentReturn as ReturnIcon,
  MoreVert as MoreVertIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Layout from '../../components/layout/Layout';
import salesService from '../../services/salesService';

const ViewReturnsPage = () => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [returns, setReturns] = useState([]);
  const [filteredReturns, setFilteredReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({
    page: 0,
    totalPages: 0,
    totalElements: 0,
    size: 10
  });
  const [returnDetailOpen, setReturnDetailOpen] = useState(false);
  const [selectedReturn, setSelectedReturn] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedReturnId, setSelectedReturnId] = useState(null);
  
  // Menu state
  const isMenuOpen = Boolean(menuAnchorEl);

  // Fetch returns with pagination
  const fetchReturns = useCallback(async () => {
    setLoading(true);
    try {
      // Call the API to get all returned orders
      const result = await salesService.getReturnedOrders();
      
      console.log('Fetched return orders:', result);
      
      // Process the data and enrich with customer info if needed
      const returnsData = Array.isArray(result) ? result : [];
      
      // Filter for orders with orderNumber starting with "RETURN-"
      const filteredReturnsData = returnsData.filter(order => 
        order.status === 'RETURNED' || 
        (order.orderNumber && order.orderNumber.startsWith('RETURN-'))
      );
      
      // For each return, fetch the customer data if the order doesn't have customer name
      const enrichedReturns = await Promise.all(filteredReturnsData.map(async (order) => {
        // Create a new order object with properly mapped fields
        const enhancedOrder = {
          ...order,
          // Use orderNumber if available, otherwise fallback to id
          displayId: order.orderNumber || `#${order.id}`,
        };
        
        // Determine the original order number if it's a return
        if (order.orderNumber && order.orderNumber.startsWith('RETURN-')) {
          enhancedOrder.originalOrderNumber = order.orderNumber.replace('RETURN-', '');
          enhancedOrder.isReturn = true;
        }
        
        // Format date properly - handle various date formats
        if (order.orderDate) {
          try {
            // If it's in a numeric format like 202547, convert to proper date
            if (/^\d{5,6}$/.test(order.orderDate)) {
              // Attempt to parse as YYYYDDD format (year and day of year)
              const year = order.orderDate.substring(0, 4);
              const dayOfYear = parseInt(order.orderDate.substring(4));
              
              const date = new Date(parseInt(year), 0); // Start with Jan 1
              date.setDate(dayOfYear); // Add days
              
              enhancedOrder.orderDate = `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
            } 
            // For other formats, try to format nicely
            else {
              const parsedDate = new Date(order.orderDate);
              if (!isNaN(parsedDate.getTime())) {
                enhancedOrder.orderDate = `${parsedDate.getMonth() + 1}/${parsedDate.getDate()}/${parsedDate.getFullYear()}`;
              }
            }
          } catch (error) {
            console.error('Error parsing date:', error);
          }
        }
        
        // Handle customer data with higher priority on customer object
        if (order.customer) {
          if (typeof order.customer === 'object') {
            // Extract from customer object - check for name field first, then fallback to firstName/lastName
            if (order.customer.name) {
              enhancedOrder.customerName = order.customer.name;
            } else {
              const firstName = order.customer.firstName || '';
              const lastName = order.customer.lastName || '';
              enhancedOrder.customerName = `${firstName} ${lastName}`.trim() || 'Guest Customer';
            }
          } else if (typeof order.customer === 'string' && order.customer.trim()) {
            // Use customer directly if it's a string
            enhancedOrder.customerName = order.customer;
          }
        } 
        
        // Fallback to customer lookup by ID if needed
        if (!enhancedOrder.customerName && order.customerId) {
          try {
            const customerData = await salesService.getCustomerById(order.customerId);
            if (customerData) {
              // Check for name field first as mentioned in requirements
              if (customerData.name) {
                enhancedOrder.customerName = customerData.name;
              } else {
                // Fallback to firstName/lastName if name is not available
                const firstName = customerData.firstName || '';
                const lastName = customerData.lastName || '';
                enhancedOrder.customerName = `${firstName} ${lastName}`.trim() || 'Guest Customer';
              }
            } else {
              enhancedOrder.customerName = 'Guest Customer';
            }
          } catch (error) {
            console.error(`Error fetching customer for order ${order.id}:`, error);
            enhancedOrder.customerName = 'Guest Customer';
          }
        } else if (!enhancedOrder.customerName) {
          enhancedOrder.customerName = 'Guest Customer';
        }
        
        return enhancedOrder;
      }));
      
      console.log('Processed returns data:', enrichedReturns);
      
      setReturns(enrichedReturns);
      setFilteredReturns(enrichedReturns);
      
      // Set pagination if we have that info
      if (typeof result === 'object' && result.totalPages) {
        setPagination({
          ...pagination,
          totalPages: result.totalPages,
          totalElements: result.totalElements || enrichedReturns.length
        });
      } else {
        // If we don't have pagination info, just set the total based on array length
        setPagination({
          ...pagination,
          totalPages: Math.ceil(enrichedReturns.length / pagination.size),
          totalElements: enrichedReturns.length
        });
      }
    } catch (error) {
      console.error('Error fetching return orders:', error);
      enqueueSnackbar('Failed to load returns: ' + (error.message || 'Unknown error'), { variant: 'error' });
      setReturns([]);
      setFilteredReturns([]);
    } finally {
      setLoading(false);
    }
  }, [pagination.size, enqueueSnackbar]);

  // Load returns on component mount and when pagination changes
  useEffect(() => {
    fetchReturns();
  }, [fetchReturns, pagination.page]);

  // Handle search input change and search submission
  const handleSearch = (e) => {
    e.preventDefault();
    
    if (!searchQuery.trim()) {
      setFilteredReturns(returns);
      return;
    }
    
    const query = searchQuery.toLowerCase();
    const filtered = returns.filter(ret => 
      (ret.orderNumber && ret.orderNumber.toLowerCase().includes(query)) ||
      (ret.originalOrderNumber && ret.originalOrderNumber.toLowerCase().includes(query)) ||
      (ret.customerName && ret.customerName.toLowerCase().includes(query)) ||
      (ret.status && ret.status.toLowerCase().includes(query)) ||
      (ret.notes && ret.notes.toLowerCase().includes(query)) ||
      (ret.total && ret.total.toString().includes(query))
    );
    
    setFilteredReturns(filtered);
  };

  // Handle view return details
  const handleViewReturn = async (returnOrder) => {
    setLoading(true);
    
    // Check if we need to fetch more details
    if (!returnOrder.items || returnOrder.items.length === 0) {
      try {
        // Fetch return details including items
        const fullReturnData = await salesService.getSalesOrderById(returnOrder.id);
        
        // If the backend returns order with items
        if (fullReturnData && fullReturnData.items) {
          setSelectedReturn(fullReturnData);
        } else {
          // If we need to fetch items separately
          const items = await salesService.getSalesOrderItems(returnOrder.id);
          
          // Format the items to match the expected structure
          setSelectedReturn({
            ...returnOrder,
            items: items.map(item => ({
              ...item,
              // Create a product object if it doesn't exist
              product: item.product || {
                id: item.productId,
                name: item.productName || `Product ID: ${item.productId}`
              }
            }))
          });
        }
      } catch (error) {
        console.error(`Error fetching details for return ${returnOrder.id}:`, error);
        enqueueSnackbar(`Failed to load return details: ${error.message || 'Unknown error'}`, {
          variant: 'error'
        });
        
        // Use what we have
        setSelectedReturn(returnOrder);
      } finally {
        setLoading(false);
      }
    } else {
      // Return already has items, use it directly
      setSelectedReturn(returnOrder);
      setLoading(false);
    }
    
    setReturnDetailOpen(true);
  };

  // Handle menu open
  const handleMenuOpen = (event, returnId) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedReturnId(returnId);
  };

  // Handle menu close
  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedReturnId(null);
  };

  // Handle pagination change
  const handlePageChange = (event, value) => {
    setPagination({
      ...pagination,
      page: value - 1 // MUI pagination is 1-based, backend is 0-based
    });
  };

  // Status chip color mapping
  const getStatusColor = (status) => {
    switch(status) {
      case 'RETURNED': return 'info';
      case 'PENDING': return 'warning';
      case 'PROCESSING': return 'info';
      case 'SHIPPED': return 'primary';
      case 'DELIVERED': return 'success';
      case 'CANCELLED': return 'error';
      default: return 'default';
    }
  };

  // Payment status chip color mapping
  const getPaymentStatusColor = (status) => {
    switch(status) {
      case 'PAID': return 'success';
      case 'PENDING': return 'warning';
      case 'FAILED': return 'error';
      case 'REFUNDED': return 'info';
      default: return 'default';
    }
  };

  // Payment method display formatting
  const formatPaymentMethod = (method) => {
    if (!method) return 'Unknown';
    
    return method
      .split('_')
      .map(word => word.charAt(0) + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Handle Print Return Receipt
  const handlePrintReturnReceipt = async (returnId) => {
    try {
      setLoading(true);
      
      // First, get the return details to find the original order
      const returnDetails = await salesService.getSalesOrderById(returnId);
      
      // Generate receipt
      const receiptBlob = await salesService.generateReceipt(returnId);
      
      // Create URL for the blob
      const url = window.URL.createObjectURL(receiptBlob);
      
      // Prepare filename for download
      const fileName = `Return-Receipt-${returnId}.pdf`;
      
      // Create a hidden anchor element for downloading
      const downloadLink = document.createElement('a');
      downloadLink.href = url;
      downloadLink.download = fileName;
      downloadLink.target = '_blank';
      
      // Add to DOM, click, then remove
      document.body.appendChild(downloadLink);
      
      // First open for viewing in new tab
      window.open(url, '_blank');
      
      // Then trigger download
      downloadLink.click();
      document.body.removeChild(downloadLink);
      
      // Show success message
      enqueueSnackbar('Return receipt downloaded successfully', { variant: 'success' });
      
      // Cleanup
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 100);
      
    } catch (error) {
      console.error(`Error generating return receipt for order ${returnId}:`, error);
      enqueueSnackbar('Failed to generate return receipt', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3, width: '100%' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center' }}>
            <ReturnIcon sx={{ mr: 1 }} />
            Returns
        </Typography>
        </Box>

        {/* Search and filters */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', alignItems: 'center' }}>
            <TextField
              fullWidth
              placeholder="Search by order ID, customer, return reason, or status..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            <Button type="submit" variant="contained" sx={{ ml: 2 }}>
              Search
            </Button>
            <IconButton sx={{ ml: 1 }}>
              <FilterIcon />
            </IconButton>
          </Box>
        </Paper>

        {/* Loading indicator */}
        {loading && <LinearProgress sx={{ mb: 2 }} />}

        {/* Returns table */}
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell>Return #</TableCell>
                <TableCell>Original Order</TableCell>
                <TableCell>Date</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Payment Status</TableCell>
                <TableCell>Total</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredReturns.length > 0 ? (
                filteredReturns
                  .slice(pagination.page * pagination.size, (pagination.page + 1) * pagination.size)
                  .map((returnOrder) => (
                  <TableRow key={returnOrder.id} hover>
                    <TableCell>
                      {returnOrder.orderNumber || `#${returnOrder.id}`}
                    </TableCell>
                    <TableCell>
                      {returnOrder.originalOrderNumber || 'N/A'}
                    </TableCell>
                    <TableCell>
                      {returnOrder.orderDate || 'N/A'}
                    </TableCell>
                    <TableCell>{returnOrder.customerName || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={returnOrder.status || 'N/A'} 
                        size="small" 
                        color={getStatusColor(returnOrder.status)} 
                      />
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={returnOrder.paymentStatus || 'N/A'} 
                        size="small" 
                        color={getPaymentStatusColor(returnOrder.paymentStatus)} 
                      />
                    </TableCell>
                    <TableCell>
                      {returnOrder.total 
                        ? new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD'
                          }).format(returnOrder.total)
                        : 'N/A'
                      }
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex' }}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleViewReturn(returnOrder)}
                          title="View Return Details"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        <IconButton 
                          size="small"
                          onClick={() => handlePrintReturnReceipt(returnOrder.id)}
                          title="Print Return Receipt"
                        >
                          <PrintIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, returnOrder.id)}
                          title="More Actions"
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    {loading ? 'Loading returns...' : 'No returns found'}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {filteredReturns.length > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination 
              count={pagination.totalPages} 
              page={pagination.page + 1} 
              onChange={handlePageChange} 
              color="primary" 
            />
          </Box>
        )}

        {/* Action Menu */}
        <Menu
          anchorEl={menuAnchorEl}
          open={isMenuOpen}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => {
            if (selectedReturnId) {
              handlePrintReturnReceipt(selectedReturnId);
            }
            handleMenuClose();
          }}>
            <PrintIcon fontSize="small" sx={{ mr: 1 }} />
            Print Return Receipt
          </MenuItem>
          <MenuItem onClick={() => {
            if (selectedReturnId) {
              handleViewReturn(returns.find(r => r.id === selectedReturnId));
            }
            handleMenuClose();
          }}>
            <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
            View Return Details
          </MenuItem>
        </Menu>

        {/* Return Details Dialog */}
        <Dialog
          open={returnDetailOpen}
          onClose={() => setReturnDetailOpen(false)}
          aria-labelledby="return-details-dialog-title"
          maxWidth="md"
          fullWidth
        >
          <DialogTitle id="return-details-dialog-title">
            Return Details
          </DialogTitle>
          <DialogContent dividers>
            {selectedReturn ? (
                  <Box>
                {/* Return header info */}
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Return Number</Typography>
                    <Typography variant="body1">{selectedReturn.orderNumber || `#${selectedReturn.id}`}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Original Order</Typography>
                    <Typography variant="body1">{selectedReturn.originalOrderNumber || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Return Date</Typography>
                    <Typography variant="body1">{selectedReturn.orderDate || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Customer</Typography>
                    <Typography variant="body1">{selectedReturn.customerName || 'N/A'}</Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Status</Typography>
                    <Chip 
                      label={selectedReturn.status || 'N/A'} 
                      size="small" 
                      color={getStatusColor(selectedReturn.status)} 
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="textSecondary">Payment Status</Typography>
                    <Chip 
                      label={selectedReturn.paymentStatus || 'N/A'} 
                      size="small" 
                      color={getPaymentStatusColor(selectedReturn.paymentStatus)} 
                    />
                  </Grid>
                  {selectedReturn.notes && (
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">Return Reason/Notes</Typography>
                      <Typography variant="body1">{selectedReturn.notes}</Typography>
                    </Grid>
                  )}
                </Grid>

                <Divider sx={{ my: 2 }} />

                {/* Return items */}
                <Typography variant="h6" gutterBottom>Returned Items</Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
          <TableHead>
            <TableRow>
                        <TableCell>Product</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
                      {selectedReturn.items && selectedReturn.items.length > 0 ? (
                        selectedReturn.items.map((item) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              {(item.product && item.product.name) || 
                               item.productName || 
                               `Product #${item.productId}`}
                            </TableCell>
                            <TableCell align="right">{item.quantity}</TableCell>
                            <TableCell align="right">
                              {item.unitPrice 
                                ? new Intl.NumberFormat('en-US', {
                                    style: 'currency',
                                    currency: 'USD'
                                  }).format(item.unitPrice)
                                : 'N/A'
                              }
                            </TableCell>
                            <TableCell align="right">
                              {(item.total || (item.unitPrice && item.quantity))
                                ? new Intl.NumberFormat('en-US', {
                                    style: 'currency',
                                    currency: 'USD'
                                  }).format(item.total || (item.unitPrice * item.quantity))
                                : 'N/A'
                              }
                            </TableCell>
                            </TableRow>
                        ))
                      ) : (
                        <TableRow>
                          <TableCell colSpan={4} align="center">No items found</TableCell>
              </TableRow>
                      )}
          </TableBody>
        </Table>
      </TableContainer>
                
                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={6} md={8}></Grid>
                    <Grid item xs={6} md={4}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body2">Subtotal:</Typography>
                    <Typography variant="body2">
                          {selectedReturn.subtotal 
                            ? new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD'
                              }).format(selectedReturn.subtotal)
                            : 'N/A'
                          }
                    </Typography>
                      </Box>
                      {selectedReturn.discount && selectedReturn.discount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Discount:</Typography>
                          <Typography variant="body2">
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD'
                            }).format(selectedReturn.discount)}
                      </Typography>
                        </Box>
                    )}
                      {selectedReturn.taxAmount && selectedReturn.taxAmount > 0 && (
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2">Tax:</Typography>
                    <Typography variant="body2">
                            {new Intl.NumberFormat('en-US', {
                              style: 'currency',
                              currency: 'USD'
                            }).format(selectedReturn.taxAmount)}
                    </Typography>
                        </Box>
                      )}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                        <Typography variant="subtitle1" fontWeight="bold">Total:</Typography>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {selectedReturn.total 
                            ? new Intl.NumberFormat('en-US', {
                                style: 'currency',
                                currency: 'USD'
                              }).format(selectedReturn.total)
                            : 'N/A'
                          }
                    </Typography>
                  </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            ) : (
              <Typography>Loading return details...</Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setReturnDetailOpen(false)}>Close</Button>
            {selectedReturn && (
              <Button 
                variant="contained" 
                startIcon={<PrintIcon />}
                onClick={() => {
                  handlePrintReturnReceipt(selectedReturn.id);
                }}
              >
                Print Receipt
              </Button>
            )}
          </DialogActions>
        </Dialog>
    </Box>
    </Layout>
  );
};

export default ViewReturnsPage; 